import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import SpiralIcon from "@/components/SpiralIcon";
import { requestLoginLink } from "@/lib/api";

const LINK_ERRORS: Record<string, string> = {
  invalid: "リンクが正しくありません。もう一度送信してください。",
  expired: "リンクの有効期限が切れています。もう一度送信してください。",
};

export default function Login() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(
    LINK_ERRORS[params.get("error") ?? ""] ?? null,
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    setError(null);
    try {
      await requestLoginLink(email);
      setSent(true);
    } catch {
      setError("送信に失敗しました。時間をおいて試してください。");
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="bg-slate-50 dark:bg-[#020617] text-slate-900 dark:text-slate-100 flex items-center justify-center px-5"
      style={{ minHeight: "100dvh" }}
    >
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-10">
          <SpiralIcon className="h-16 w-auto text-blue-600 dark:text-blue-400" />
          <h1 className="mt-4 text-2xl font-black font-heading">locomoco</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            エンジニアのための英単語アプリ
          </p>
        </div>

        {sent ? (
          <div className="text-center space-y-3">
            <CheckCircle2
              size={40}
              className="mx-auto text-emerald-500"
              aria-hidden
            />
            <p className="font-bold">メールを送りました</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
              <span className="font-medium break-all">{email}</span> 宛のリンクを
              開くとログインできます。15分で失効します。
            </p>
            <button
              onClick={() => setSent(false)}
              className="text-sm text-blue-600 dark:text-blue-400 underline underline-offset-4 pt-2"
            >
              別のアドレスで送り直す
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block">
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                メールアドレス
              </span>
              <div className="mt-1.5 relative">
                <Mail
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  aria-hidden
                />
                <input
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-10 pr-3 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 outline-none focus:border-blue-500 transition-colors"
                />
              </div>
            </label>

            {error && (
              <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={sending}
              className="w-full py-3 rounded-xl bg-blue-600 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-transform"
            >
              <Send size={18} aria-hidden />
              {sending ? "送信中…" : "ログインリンクを送る"}
            </button>

            <p className="text-xs text-slate-400 text-center leading-relaxed">
              パスワードはありません。届いたリンクを開くだけでログインできます。
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
